import type { ActionDef } from '@/api/actions'

/**
 * "New moves the studio will learn: juggling" (docs/SKEL-STORY.md R7) — a cheap, client-side
 * guess at which verbs of the idea no action in the registry covers, so the parent knows the
 * studio will teach the puppets something new while it writes the story.
 *
 * It reads the idea the way the planner's offline reading does (backend `app/story/plan.py`
 * `mock_plan`): a verb is a word right after a friend's name or a joining word ("the clown
 * **juggles**", "then **throws**", "they **dance**"); it is new when none of its forms is an action
 * id, the first word of an id or label, or a verb the studio has always known. The real planner
 * decides for itself; the story page shows what it actually learned.
 */

/** A story plans at most this many new moves (R5). */
export const MAX_NEW_MOVES = 2

const words = (s: string): string[] => s.split(/\s+/).filter(Boolean)

/** Never a verb (backend `plan._NOT_VERBS`). */
const NOT_VERBS = new Set(
  words(`the a an and then to at by in on of for with from into near under over up down is are was were be has have
  had very so too also all some one two three it its they them he she his her their we our you your my me i who that
  this these those there here when while after before again each other friend friends together happy sad big little
  small day sunny morning night time once upon not no yes but or because wants want tries try likes like loves love
  can will would decides starts begins goes go went comes come gets get lets let makes make does do did finally
  suddenly later soon now`),
)

/** The next word is where a verb stands (backend `plan._VERB_LEADS`). */
const VERB_LEADS = new Set(
  words(`then and to they he she it we can will both together who friends kids children everyone everybody animals`),
)

/**
 * Verbs the studio always knew (the Part 2 verbs, their idea forms and aliases, the gestures) and
 * verbs a story tells rather than acts (said, felt, thought — the narrator carries them): never new.
 */
const FAMILIAR = new Set(
  words(`act admire bite bring carry catch collect cuddle dance drive drop eat emote enter face find fly give go grab hand
  hide hold hop hug jump kick look move munch nap narrate nibble offer pick pickup place plant play point pull push put
  ride roll run sail say see share sit sleep spot swim take throw toss wait walk watch wear
  wave smile laugh giggle cheer nod clap bow cry shout call talk speak tell ask answer sing hum whisper
  think wonder feel need hear listen know learn remember forget stop start stay rest dream wish hope meet visit leave
  arrive return help search keep grow count`),
)

/** Every form a word may be the base of ("juggles" → juggle, "hopping" → hop, "tossed" → toss). */
export function stems(word: string): string[] {
  const w = word.toLowerCase()
  const out = [w]
  if (w.endsWith('ies') && w.length > 4) out.push(`${w.slice(0, -3)}y`)
  if (w.endsWith('es') && w.length > 4) out.push(w.slice(0, -2))
  if (w.endsWith('s') && !w.endsWith('ss') && w.length > 3) out.push(w.slice(0, -1))
  if (w.endsWith('ing') && w.length > 5) {
    const base = w.slice(0, -3)
    out.push(base, `${base}e`)
    if (base.length > 2 && base.at(-1) === base.at(-2)) out.push(base.slice(0, -1))
  }
  if (w.endsWith('ed') && w.length > 4) {
    out.push(w.slice(0, -2), w.slice(0, -1))
    if (w.length > 5 && w.at(-3) === w.at(-4)) out.push(w.slice(0, -3))
  }
  return [...new Set(out)]
}

/** A new move's name from the idea's word (backend `plan._base_form`). */
export function baseForm(word: string): string {
  const w = word.toLowerCase()
  if (w.endsWith('ies') && w.length > 4) return `${w.slice(0, -3)}y`
  if (/(ches|shes|sses|xes|zes)$/.test(w)) return w.slice(0, -2)
  if (w.endsWith('s') && !w.endsWith('ss') && w.length > 3) return w.slice(0, -1)
  if (w.endsWith('ing') && w.length > 5) {
    const base = w.slice(0, -3)
    return base.length > 2 && base.at(-1) === base.at(-2) ? base.slice(0, -1) : base
  }
  return w
}

/** "juggle" → "juggling", "hop" → "hopping" (backend `plan.gerund`). */
export function gerund(word: string): string {
  const w = (word.split(/\s+/)[0] ?? word).toLowerCase()
  if (w.endsWith('ie')) return `${w.slice(0, -2)}ying`
  if (w.endsWith('e') && !/(ee|ye|oe)$/.test(w) && w.length > 2) return `${w.slice(0, -1)}ing`
  const vowel = (c: string | undefined): boolean => c !== undefined && 'aeiou'.includes(c)
  if (w.length >= 3 && w.length <= 4 && !vowel(w.at(-1)) && !'wxy'.includes(w.at(-1)!) && vowel(w.at(-2)) && !vowel(w.at(-3))) return `${w}${w.at(-1)}ing`
  return `${w}ing`
}

/** Words that name a move the registry has: each id, its first word, the first word of its label. */
export function knownMoveWords(actions: ReadonlyArray<Pick<ActionDef, 'id' | 'label'>>): Set<string> {
  const out = new Set<string>()
  for (const a of actions) {
    out.add(a.id.toLowerCase())
    const first = a.id.toLowerCase().split('_')[0]
    if (first && first.length >= 3) out.add(first)
    const label = (a.label ?? '').toLowerCase().match(/[a-z]+/)?.[0]
    if (label && label.length >= 3) out.add(label)
  }
  return out
}

const singular = (w: string): string => (w.endsWith('s') && !w.endsWith('ss') && w.length > 3 ? w.slice(0, -1) : w)

/**
 * The idea's verbs no known move covers, as base forms in the idea's order (at most
 * `MAX_NEW_MOVES`). `castNames` are the friends' names ("Clown", "Little Fox").
 */
export function newMoves(idea: string, castNames: readonly string[], known: ReadonlySet<string>): string[] {
  const raw = idea.toLowerCase().match(/[a-z]+/g) ?? []
  const names = new Set(castNames.flatMap((n) => n.toLowerCase().match(/[a-z]+/g) ?? []).map(singular))
  const isName = (w: string | undefined): boolean => w !== undefined && names.has(singular(w))
  const out: string[] = []
  raw.forEach((w, i) => {
    if (out.length >= MAX_NEW_MOVES || NOT_VERBS.has(w) || isName(w) || w.length < 4 || w.endsWith('ly')) return
    let j = i - 1
    if (j > 0 && raw[j]!.endsWith('ly') && raw[j]!.length > 3) j -= 1 // "the fox quickly throws"
    if (!(j >= 0 && (VERB_LEADS.has(raw[j]!) || isName(raw[j])))) return
    const forms = stems(w)
    if (forms.some((s) => known.has(s) || FAMILIAR.has(s))) return
    const base = baseForm(w)
    if (!out.includes(base)) out.push(base)
  })
  return out
}
